// "Positions you misplay": over the last 30 days, the reader's average score
// change in pawns per middlegame in each kind of position
// (`GameFacts.middlegamePositions`, the middlegame-positions fact). A game
// counts in every kind it has; its change in a kind is the change its moves
// in that kind carry. Kinds no game has are left out, and so are kinds in
// fewer than `MIN_POSITION_GAMES` middlegames (counted in `hidden`), whose
// average rests on too few games to rank (2026-10-10: "Exchange up" from 2
// games stood above kinds of 100); the rest come worst first, and only the
// worst `MAX_POSITION_ROWS` are kept, so the card stays short. Registered
// with the game store by this plugin's setup.
import { aggregatorId, MIDDLEGAME_POSITIONS, type Accumulator, type Aggregator, type MiddlegamePosition } from '@chess/api-model';

/** A kind of position is ranked only from this many middlegames: below it, one bad game decides its average. */
export const MIN_POSITION_GAMES = 5;

/** The card lists at most this many kinds, the worst: the ones to work on. */
export const MAX_POSITION_ROWS = 5;

/** One kind of position over the period. */
export interface PositionRow {
  kind: MiddlegamePosition;
  /** Middlegames the reader played in positions of this kind (the games the library filter lists). */
  games: number;
  /** Pawns, the average per middlegame of the score change carried by the reader's moves in this kind. */
  average: number;
}

/** What the card shows; plain data, so the card and its stories need no store. */
export interface MiddlegamePositionsResult {
  /** Scanned middlegames of the period, whatever their kinds. */
  middlegames: number;
  /** The worst `MAX_POSITION_ROWS` of the kinds in at least `MIN_POSITION_GAMES` middlegames, the lowest average first; ties by more games. */
  rows: PositionRow[];
  /** Kinds in at least `MIN_POSITION_GAMES` middlegames, listed in `rows` or not. */
  ranked: number;
  /** Kinds that occur in fewer than `MIN_POSITION_GAMES` middlegames, left out of `rows`. */
  hidden: number;
}

export class MiddlegamePositionsAggregator implements Aggregator<MiddlegamePositionsResult> {
  readonly id = aggregatorId('middlegame-positions');

  start(): Accumulator<MiddlegamePositionsResult> {
    const games = new Map<MiddlegamePosition, number>();
    const change = new Map<MiddlegamePosition, number>();
    let middlegames = 0;
    return {
      add: (game) => {
        const shares = game.middlegamePositions;
        if (!game.scanned || !shares) return;
        middlegames += 1;
        for (const kind of MIDDLEGAME_POSITIONS) {
          const share = shares[kind];
          if (!share) continue;
          games.set(kind, (games.get(kind) ?? 0) + 1);
          change.set(kind, (change.get(kind) ?? 0) + share.change);
        }
      },
      result: () => {
        const all = [...games].map(([kind, n]) => ({ kind, games: n, average: (change.get(kind) ?? 0) / n }));
        const ranked = all.filter((r) => r.games >= MIN_POSITION_GAMES).sort((a, b) => a.average - b.average || b.games - a.games);
        return { middlegames, rows: ranked.slice(0, MAX_POSITION_ROWS), ranked: ranked.length, hidden: all.length - ranked.length };
      },
    };
  }
}
